import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { isPlyxStandaloneMode } from "@/lib/plyxStandalone";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const PlyxRegister = () => {
  const [form, setForm] = useState({ handle: "", display_name: "", email: "", password: "", country: "", address: "" });
  const [loading, setLoading] = useState(false);
  const { register } = usePlyxAuth();
  const navigate = useNavigate();

  const onChange = (k) => (e) => {
    const v = k === "handle" ? e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.address || form.address.trim().length < 5) { toast.error("Please enter your full delivery address"); return; }
    setLoading(true);
    try {
      await register(form);
      toast.success(`Welcome to Plyx, @${form.handle}`);
      navigate("/plyx", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6 py-16" data-testid="plyx-register-page">
      <div className="w-full max-w-md">
        <h1 className="font-display text-4xl sm:text-5xl mb-2 tracking-tight">Join Plyx</h1>
        <p className="text-white/60 mb-8">
          {isPlyxStandaloneMode()
            ? "Create your Plyx account — start watching, posting and earning in minutes."
            : "A separate Plyx account — you don't need AfriMarket. Link it later if you do."}
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Handle</label>
            <div className="flex items-center bg-white/8 border border-white/15 rounded-md focus-within:ring-2 focus-within:ring-[#FF2D7A]/50">
              <span className="pl-3 text-white/50">@</span>
              <input
                required minLength={3} maxLength={24}
                value={form.handle}
                onChange={onChange("handle")}
                data-testid="plyx-register-handle"
                className="plyx-input flex-1 px-2 py-3 bg-transparent focus:outline-none"
                placeholder="yourname"
              />
            </div>
            <div className="text-xs text-white/40 mt-1">3–24 letters, numbers or underscores.</div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Display name</label>
            <input
              required maxLength={60}
              value={form.display_name}
              onChange={onChange("display_name")}
              data-testid="plyx-register-display-name"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Email</label>
            <input
              type="email" required
              value={form.email}
              onChange={onChange("email")}
              data-testid="plyx-register-email"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Password</label>
            <input
              type="password" required minLength={6}
              value={form.password}
              onChange={onChange("password")}
              data-testid="plyx-register-password"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Country (optional)</label>
            <input
              maxLength={60}
              value={form.country}
              onChange={onChange("country")}
              data-testid="plyx-register-country"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
              placeholder="Nigeria"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Delivery address</label>
            <textarea
              required maxLength={400} rows={3}
              value={form.address}
              onChange={onChange("address")}
              data-testid="plyx-register-address"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
              placeholder="House number, street, city, state/region, postcode"
            />
            <div className="text-xs text-white/40 mt-1">Where we deliver your orders. You can change it later in settings.</div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="plyx-register-submit"
            className="w-full py-3 rounded-full font-semibold text-[#0A0A0A] disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Create Plyx account"}
          </button>
        </form>
        <div className="mt-6 text-center text-sm text-white/60">
          Already on Plyx?{" "}
          <Link to="/plyx/login" data-testid="plyx-register-to-login" className="underline hover:text-white">Log in</Link>
        </div>
      </div>
    </div>
  );
};

export default PlyxRegister;
